import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useOnshapeContext } from '@/hooks/useOnshapeContext';
import { getHardwareContext } from '@/lib/onshape-api';
import { HardwareImport } from './HardwareImport';
import { HardwareLibrary } from './HardwareLibrary';

const ELEMENT_TYPE_KEY = 'otto_element_types';

/**
 * Element type without spending an Onshape API call when possible:
 *  1. `?context=assembly` (or partstudio/drawing) set on the extension's action URL
 *     in the Onshape developer portal — free on every open.
 *  2. A per-browser memo by elementId — a tab's type never changes.
 */
function knownElementType(elementId) {
  const context = new URLSearchParams(window.location.search).get('context');
  if (context) return context.replace(/[^a-z]/gi, '').toUpperCase();
  try {
    return JSON.parse(localStorage.getItem(ELEMENT_TYPE_KEY) || '{}')[elementId] || null;
  } catch {
    return null;
  }
}

function rememberElementType(elementId, elementType) {
  try {
    const types = JSON.parse(localStorage.getItem(ELEMENT_TYPE_KEY) || '{}');
    types[elementId] = elementType;
    localStorage.setItem(ELEMENT_TYPE_KEY, JSON.stringify(types));
  } catch {
    // Only a convenience; the next open just asks again.
  }
}

/**
 * Otto's home screen picks the hardware tool by where it was opened:
 * an Assembly gets the tool belt (insert from your part library), anything
 * else gets Add Hardware (vendor files → attributed Part Studio).
 */
export function HardwareHome() {
  const { documentId, workspaceId, elementId, isValid } = useOnshapeContext();
  const [elementType, setElementType] = useState(() => (isValid ? knownElementType(elementId) : null));
  const [resolved, setResolved] = useState(!isValid || !!elementType);

  useEffect(() => {
    if (!isValid || elementType) return;
    let cancelled = false;
    getHardwareContext({ documentId, workspaceId, elementId })
      .then((res) => {
        if (cancelled) return;
        setElementType(res.elementType);
        if (res.elementType) rememberElementType(elementId, res.elementType);
      })
      .catch((err) => console.warn('[HardwareHome] element type lookup failed:', err.message))
      .finally(() => !cancelled && setResolved(true));
    return () => { cancelled = true; };
  }, [documentId, workspaceId, elementId, isValid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!resolved) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  return elementType === 'ASSEMBLY' ? <HardwareLibrary /> : <HardwareImport />;
}
